import { Briefcase, CalendarOff, ChevronDown, Clock } from "lucide-react";
import { useState } from "react";
import { TimeField } from "@/components/TimeField";
import { formatDuration, minutesSinceMidnight } from "@/lib/clock";
import type { WorkdayLogConfig, WorkdayLogDayData } from "@/lib/schema";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { isWorkDuration } from "./duration";
import { earnedPeriods, hoursPoints, loggedMinutes, scoreDay } from "./scoring";

type Props = WidgetDayViewProps<WorkdayLogConfig>;

const PREVIEW_DATA: WorkdayLogDayData = {
  duration: "07:20",
  periods: [true, false],
};

export function DayView({
  config,
  data,
  dayKey,
  onChangeDay,
  readOnly = false,
}: Props) {
  const realData = (data ?? {}) as WorkdayLogDayData;
  const hasAny = realData.duration !== undefined || realData.notWorked === true;
  const showPreview = readOnly && !hasAny;
  const shown = showPreview ? PREVIEW_DATA : realData;

  const notWorked = shown.notWorked === true;
  const minutes = loggedMinutes(shown);
  const earned = earnedPeriods(config, shown);
  const answers = shown.periods ?? [];
  const hours = hoursPoints(config, shown);
  const score = scoreDay(config, shown);

  const periodMinutes = minutesSinceMidnight(config.periodDuration);
  const targetMinutes = minutesSinceMidnight(config.targetDuration);
  // How much further until the next question opens — the rule is a floor, so
  // without this the card gives no hint why a 3t 20m day earned nothing.
  const untilNextPeriod =
    periodMinutes !== null && periodMinutes > 0 && !notWorked
      ? periodMinutes * (earned + 1) - minutes
      : null;

  const writeDay = (patch: Partial<WorkdayLogDayData>) => {
    if (readOnly) return;
    onChangeDay({ ...realData, ...patch } satisfies WorkdayLogDayData);
  };

  // Invariant: a logged duration and "Ikke jobbet" never coexist. Logging
  // clears the marker; clearing the duration again does not restore it.
  const handleDuration = (next: string | undefined) => {
    writeDay({ duration: next, notWorked: false });
  };

  // True while focus sits in the duration field.
  const [editing, setEditing] = useState(false);

  const handleDurationBlur = () => {
    setEditing(false);
    // Prune answers the day no longer earns. Scoring already ignores them
    // unconditionally, so this is hygiene — but it must wait for the field
    // to settle: "0" parses to 00:00 on the way to "0720", and pruning on
    // every keystroke would eat the answers before the real value lands.
    const stored = realData.periods ?? [];
    const keep = earnedPeriods(config, realData);
    if (stored.length > keep) writeDay({ periods: stored.slice(0, keep) });
  };

  const answerPeriod = (index: number, value: boolean) => {
    if (readOnly) return;
    const next: (boolean | null)[] = Array.from(
      { length: earned },
      (_, i) => answers[i] ?? null,
    );
    // Clicking the answer already given takes it back to unanswered.
    next[index] = next[index] === value ? null : value;
    writeDay({ periods: next });
  };

  const toggleNotWorked = () => {
    if (readOnly) return;
    if (realData.notWorked === true) {
      writeDay({ notWorked: false });
      return;
    }
    writeDay({ duration: undefined, notWorked: true, periods: [] });
  };

  // Ferdiglogget (CONTEXT.md): the marker, or a duration with every earned
  // period answered — Ja or Nei alike, since reporting an unproductive
  // stretch is logging it, not leaving it blank. A day that earned no
  // period has nothing left to answer, so a one-hour Saturday closes as
  // soon as its duration is in.
  const realEarned = earnedPeriods(config, realData);
  const realAnswers = realData.periods ?? [];
  const allAnswered = Array.from({ length: realEarned }).every(
    (_, i) => realAnswers[i] === true || realAnswers[i] === false,
  );
  const loggingCompleted =
    !readOnly &&
    !editing &&
    (realData.notWorked === true ||
      (realData.duration !== undefined && allAnswered));

  // Not ready while the field has focus: `loggingCompleted` is deferred past
  // editing, so a focus round-trip flickers it false and back. That is not the
  // day becoming finished, and treating it as one would fold a card away
  // between two corrections.
  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted, !editing);
  const collapsible = loggingCompleted;
  const collapsed = collapsible && !open;

  const productiveCount = Array.from({ length: realEarned }).filter(
    (_, i) => realAnswers[i] === true,
  ).length;

  return (
    <section
      className={`widget-card workday-log${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            <b>{score}</b>
            <span className="max">p</span>
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`widget-summary-${config.id}`}
        >
          <span className="summary-line">
            {realData.notWorked === true ? (
              <>
                <CalendarOff
                  className="summary-icon"
                  size={14}
                  aria-hidden="true"
                />
                Ikke jobbet
              </>
            ) : (
              <>
                <Briefcase
                  className="summary-icon"
                  size={14}
                  aria-hidden="true"
                />
                {formatDuration(loggedMinutes(realData))}
                {realEarned > 0
                  ? ` · ${productiveCount} av ${realEarned} produktive`
                  : ""}
              </>
            )}
          </span>
        </div>
      ) : null}
      <div className="widget-card-body">
        <div className="widget-card-submeta">
          Mål {targetMinutes !== null ? formatDuration(targetMinutes) : "—"} ·{" "}
          {config.pointsAtTarget}p · periode{" "}
          {periodMinutes !== null ? formatDuration(periodMinutes) : "—"} ·{" "}
          {config.pointsPerPeriod}p
        </div>

        {/* Focus is tracked on the wrapper so pruning runs when the field
            settles, not on the keystroke that momentarily reads 00:00. */}
        <div
          className="workday-log-duration-row"
          onFocus={() => setEditing(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              handleDurationBlur();
            }
          }}
        >
          <label
            className="workday-log-duration-label"
            htmlFor={`duration-${config.id}`}
          >
            <Clock size={14} aria-hidden="true" />
            Arbeidstid
          </label>
          <TimeField
            id={`duration-${config.id}`}
            value={shown.duration}
            disabled={readOnly || notWorked}
            accept={isWorkDuration}
            onChange={handleDuration}
            data-testid={`workday-log-duration-input-${config.id}`}
          />
          <span
            className="workday-log-duration-note"
            data-testid={`workday-log-duration-note-${config.id}`}
          >
            {notWorked
              ? "ikke jobbet"
              : shown.duration !== undefined
                ? formatDuration(minutes)
                : "ikke logget"}
          </span>
          <span
            className="workday-log-duration-score"
            data-testid={`workday-log-hours-score-${config.id}`}
          >
            {hours}p
          </span>
        </div>

        <div
          className="workday-log-periods"
          data-testid={`workday-log-periods-${config.id}`}
        >
          {earned === 0 ? (
            <p
              className="hint"
              data-testid={`workday-log-no-periods-${config.id}`}
            >
              {notWorked
                ? "Ingen perioder på en dag uten arbeid."
                : untilNextPeriod !== null && shown.duration !== undefined
                  ? `Ingen hel periode enda — ${formatDuration(untilNextPeriod)} igjen til det første spørsmålet.`
                  : "Logg arbeidstid for å få spørsmål om produktivitet."}
            </p>
          ) : (
            Array.from({ length: earned }, (_, index) => {
              const answer = answers[index] ?? null;
              return (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: the index IS a period's identity — periods are positional, never reordered or inserted, and answers are stored against the same index.
                  key={`period-${config.id}-${index}`}
                  className="workday-log-period"
                  data-testid={`workday-log-period-${index}-${config.id}`}
                >
                  <span className="workday-log-period-label">
                    Periode {index + 1}
                    <small>Var du produktiv?</small>
                  </span>
                  <div className="workday-log-period-answer">
                    <button
                      type="button"
                      className={answer === true ? "btn-primary" : "btn ghost"}
                      disabled={readOnly}
                      aria-pressed={answer === true}
                      onClick={() => answerPeriod(index, true)}
                      data-testid={`workday-log-period-yes-${index}-${config.id}`}
                    >
                      Ja
                    </button>
                    <button
                      type="button"
                      className={answer === false ? "btn-primary" : "btn ghost"}
                      disabled={readOnly}
                      aria-pressed={answer === false}
                      onClick={() => answerPeriod(index, false)}
                      data-testid={`workday-log-period-no-${index}-${config.id}`}
                    >
                      Nei
                    </button>
                  </div>
                  <span
                    className="workday-log-period-score"
                    data-testid={`workday-log-period-score-${index}-${config.id}`}
                  >
                    {answer === true ? config.pointsPerPeriod : 0}p
                  </span>
                </div>
              );
            })
          )}
          {earned > 0 && untilNextPeriod !== null ? (
            <p
              className="hint"
              data-testid={`workday-log-next-period-${config.id}`}
            >
              {formatDuration(untilNextPeriod)} igjen til neste periode.
            </p>
          ) : null}
        </div>

        {/* Only reachable with no duration logged — the invariant forbids a
            marker alongside one, and logging clears it anyway. */}
        {!readOnly && realData.duration === undefined ? (
          <div className="workday-log-actions">
            <button
              type="button"
              className={
                realData.notWorked === true ? "btn ghost" : "btn-primary"
              }
              onClick={toggleNotWorked}
              data-testid={`workday-log-not-worked-button-${config.id}`}
            >
              {realData.notWorked === true
                ? "Fjern «ikke jobbet»"
                : "Ikke jobbet"}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
